import { useState, useCallback } from 'react'
import { useAtom } from 'jotai'
import { useToast } from '@/hooks/use-toast'
import { addChartAtom, createStandardChartObject, ChartData as LocalChartData } from '../store/chartAtoms'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'
import { organizationService } from '../services/organizationService'
import { DataSource } from '@shared'
import { parseIntent, ParsedIntent } from '@/lib/intentParser'
import debounce from 'lodash.debounce'

interface ChartPreview {
  chartData: Partial<LocalChartData>;
  rawResponse: any;
}

export const useAiChartAssistant = (dataSources: DataSource[], onClose: () => void) => {
  const [, addChart] = useAtom(addChartAtom)
  const { toast } = useToast()

  const [intent, setIntent] = useState('')
  const [parsedIntent, setParsedIntent] = useState<ParsedIntent | null>(null)
  const [preview, setPreview] = useState<ChartPreview | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generatePreview = async (currentIntent: string) => {
    if (!currentIntent.trim()) {
      setPreview(null)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    const parsed = parseIntent(currentIntent)
    setParsedIntent(parsed)

    try {
      const connectedSources = dataSources.filter(ds => ds.status === 'connected' || ds.status === 'active')
      if (connectedSources.length === 0) {
        // Handle sample data preview later if needed
        setIsLoading(false)
        setError("No connected data sources. AI assistant requires a connection.")
        return
      }

      const payload = {
        organizationId: organizationService.getCurrentOrganizationId(),
        intent: currentIntent,
        parsedIntent: {
          metrics: parsed.metrics,
          dimensions: parsed.dimensions,
          dateRange: parsed.dateRange || 'last 30 days',
          chartType: parsed.chartType || 'bar',
        },
        availableDataSources: connectedSources.map(s => s.id),
      }

      const generateChartFunction = httpsCallable(functions, 'generateChartFromIntent')
      const result = await generateChartFunction(payload)
      const resultData = result.data as any

      if (resultData.clarificationNeeded) {
        setError(resultData.message)
        setPreview(null)
      } else if (resultData.success && resultData.chartData) {
        setPreview({
          chartData: {
            ...resultData.chartData,
            chartType: parsed.chartType || resultData.chartData.chartType,
          },
          rawResponse: resultData.chartData,
        })
      } else {
        throw new Error(resultData.message || 'Failed to generate preview.')
      }
    } catch (e: any) {
      setError(e.message || 'An unknown error occurred.')
      setPreview(null)
    } finally {
      setIsLoading(false)
    }
  }

  const debouncedPreview = useCallback(debounce(generatePreview, 750), [dataSources])

  const handleIntentChange = (newIntent: string) => {
    setIntent(newIntent)
    setIsLoading(true) // Show loading immediately on change
    debouncedPreview(newIntent)
  }

  const createChart = () => {
    if (!preview || !preview.rawResponse) {
      toast({
        title: "Cannot Create Chart",
        description: "A valid preview must be generated first.",
        variant: "destructive",
      })
      return
    }

    const newChart = createStandardChartObject({
      ...preview.rawResponse,
      title: preview.rawResponse.title || intent, // Use intent as fallback title
      chartType: parsedIntent?.chartType || preview.rawResponse.chartType,
    })

    addChart(newChart)

    toast({
      title: "Chart Created! 🎉",
      description: `"${newChart.title}" has been added to your local charts.`,
    })

    onClose()
  }

  return {
    intent,
    handleIntentChange,
    preview,
    isLoading,
    error,
    createChart,
    canCreateChart: preview && !error && !isLoading,
  }
} 